/**
 * Demo Customers Configuration
 * =============================
 * ONE demo customer per industry - connected to Corvana CRM
 * Each demo represents a real Australian business scenario
 * 
 * These are the same customers that appear in:
 * - Corvana CRM (admin view)
 * - Client Dashboard (user view)
 * - Live Demo mode
 */

import { INDUSTRY_UI_CONFIG } from './industryUIConfig';

/**
 * Complete demo customers - ONE per industry
 * All connected to unified business metrics API
 */
export const DEMO_CUSTOMERS = {
  // ============================================
  // HOSPITALITY - The Continental (Premium Dining)
  // ============================================
  hospitality: {
    contact_id: 'demo-continental',
    business_id: 'continental_demo_001',
    company: 'The Continental',
    name: 'Marcus Chen',
    role: 'Owner',
    industry: 'hospitality',
    sub_industry: 'fine_dining',
    email: 'demo@thecontinental.com.au',
    phone: '(02) 9123 4567',
    mrr: 185000,
    arr: 2220000,
    tier: 'enterprise',
    health_score: 87,
    staff_count: 22,
    customer_count: 358,
    location: 'Surry Hills, Sydney NSW 2010',
    modules: ['marketing', 'operations', 'financial', 'staff', 'market_intel', 'customer'],
    tools: ['email_marketing', 'crm', 'reservations', 'inventory'],
    isDemo: true,
    type: 'demo',
    badges: ['Premium Partner', 'High Performer'],
    metrics: {
      revenue_mtd: 185000,
      revenue_change_pct: 12.5,
      profit_margin_pct: 20,
      covers_per_week: 420,
      avg_check: 85,
      table_turnover: 2.8,
      reviews_score: 4.7
    }
  },

  // ============================================
  // QSR GROUP - Craveable Brands (multi-brand portfolio)
  // ============================================
  qsr_group: {
    contact_id: 'demo_qsr_craveable',
    business_id: 'demo_qsr_craveable',
    company: 'Craveable Brands',
    name: 'Group Operations',
    role: 'Group CEO',
    industry: 'hospitality',
    sub_industry: 'qsr',
    email: 'demo@craveablebrands.com.au',
    phone: '1300 272 832',
    mrr: 480000,
    arr: 5760000,
    tier: 'enterprise',
    health_score: 90,
    staff_count: 2100,
    customer_count: 37,
    location: 'Sydney NSW (National)',
    modules: ['marketing', 'operations', 'financial', 'staff', 'market_intel', 'customer'],
    tools: ['lightspeed_pos', 'deliverect', 'drive_thru', 'loyalty'],
    isDemo: true,
    type: 'demo',
    qsr_group: true,
    tags: ['qsr-group'],
    badges: ['QSR Group', '37 Locations', '4 Brands'],
    metrics: {
      revenue_mtd: 480000,
      revenue_change_pct: 6.2,
      profit_margin_pct: 14,
      locations: 37,
      brands: 4,
      reviews_score: 4.3
    }
  },


  // ============================================
  // RETAIL - Urban Threads (Fashion Retail)
  // ============================================
  retail: {
    contact_id: 'demo-urban-threads',
    business_id: 'retail_demo_001',
    company: 'Urban Threads',
    name: 'Sarah Mitchell',
    role: 'Founder & CEO',
    industry: 'retail',
    sub_industry: 'fashion',
    email: 'demo@urbanthreads.com.au',
    phone: '1300 STYLE',
    mrr: 145000,
    arr: 1740000,
    tier: 'professional',
    health_score: 84,
    staff_count: 35,
    customer_count: 12500,
    location: 'Bondi Junction, Sydney NSW 2022',
    modules: ['marketing', 'operations', 'financial', 'customer', 'inventory'],
    tools: ['email_marketing', 'crm', 'pos', 'loyalty'],
    isDemo: true,
    type: 'demo',
    badges: ['Multi-Location', 'Retail Excellence'],
    metrics: {
      revenue_mtd: 145000,
      revenue_change_pct: 8.5,
      profit_margin_pct: 45,
      stores: 5,
      sales_per_sqm: 4850,
      conversion_rate: 3.2,
      avg_transaction: 128
    }
  },

  // ============================================
  // HEALTHCARE - Wellness Medical Centre
  // ============================================
  healthcare: {
    contact_id: 'demo-wellness-medical',
    business_id: 'healthcare_demo_001',
    company: 'Wellness Medical Centre',
    name: 'Dr. Emily Park',
    role: 'Practice Director',
    industry: 'healthcare',
    sub_industry: 'medical_practice',
    email: 'demo@wellnessmedical.com.au',
    phone: '(02) 8888 5555',
    mrr: 220000,
    arr: 2640000,
    tier: 'enterprise',
    health_score: 91,
    staff_count: 45,
    customer_count: 8500,
    location: 'Chatswood, Sydney NSW 2067',
    modules: ['marketing', 'operations', 'financial', 'staff', 'customer', 'compliance'],
    tools: ['email_marketing', 'crm', 'appointments', 'billing'],
    isDemo: true,
    type: 'demo',
    badges: ['Healthcare Certified', 'Multi-Practice'],
    metrics: {
      revenue_mtd: 220000,
      revenue_change_pct: 15.2,
      profit_margin_pct: 35,
      practices: 4,
      patient_visits: 1800,
      rebooking_rate: 78,
      satisfaction: 4.8
    }
  },

  // ============================================
  // PROFESSIONAL - Morrison & Associates Law
  // ============================================
  professional: {
    contact_id: 'demo-morrison-law',
    business_id: 'professional_demo_001',
    company: 'Morrison & Associates',
    name: 'James Morrison',
    role: 'Managing Partner',
    industry: 'professional',
    sub_industry: 'law_firm',
    email: 'demo@morrisonlaw.com.au',
    phone: '(02) 9000 1234',
    mrr: 310000,
    arr: 3720000,
    tier: 'enterprise',
    health_score: 89,
    staff_count: 28,
    customer_count: 420,
    location: 'Martin Place, Sydney NSW 2000',
    modules: ['marketing', 'operations', 'financial', 'staff', 'market_intel', 'customer', 'projections'],
    tools: ['email_marketing', 'crm', 'time_tracking', 'invoicing'],
    isDemo: true,
    type: 'demo',
    badges: ['Top Law Firm', 'Enterprise Client'],
    metrics: {
      revenue_mtd: 310000,
      revenue_change_pct: 18.5,
      profit_margin_pct: 42,
      utilization_rate: 78.5,
      billing_efficiency: 92.3,
      revenue_per_employee: 165000,
      ar_days: 32
    }
  },

  // ============================================
  // FITNESS - Peak Performance Gym
  // ============================================
  fitness: {
    contact_id: 'demo-peak-performance',
    business_id: 'fitness_demo_001',
    company: 'Peak Performance',
    name: 'Christian Robertson',
    role: 'Owner',
    industry: 'fitness',
    sub_industry: 'gym',
    email: 'demo@peakperformance.com.au',
    phone: '(02) 9876 5432',
    mrr: 95000,
    arr: 1140000,
    tier: 'professional',
    health_score: 86,
    staff_count: 18,
    customer_count: 2400,
    location: 'Newtown, Sydney NSW 2042',
    modules: ['marketing', 'operations', 'financial', 'staff', 'customer'],
    tools: ['email_marketing', 'crm', 'memberships', 'pt_booking'],
    isDemo: true,
    type: 'demo',
    badges: ['Fitness Excellence', 'High Retention'],
    metrics: {
      revenue_mtd: 95000,
      revenue_change_pct: 9.8,
      profit_margin_pct: 28,
      active_members: 2400,
      check_ins_daily: 320,
      retention_rate: 87,
      nps_score: 72
    }
  },

  // ============================================
  // PROPERTY - Harbour View Realty
  // ============================================
  property: {
    contact_id: 'demo-harbour-view',
    business_id: 'property_demo_001',
    company: 'Harbour View Realty',
    name: 'Michael Thompson',
    role: 'Principal',
    industry: 'property',
    sub_industry: 'real_estate',
    email: 'demo@harbourview.com.au',
    phone: '(02) 9555 7890',
    mrr: 175000,
    arr: 2100000,
    tier: 'professional',
    health_score: 88,
    staff_count: 22,
    customer_count: 850,
    location: 'Double Bay, Sydney NSW 2028',
    modules: ['marketing', 'operations', 'financial', 'customer', 'market_intel'],
    tools: ['email_marketing', 'crm', 'listings', 'appraisals'],
    isDemo: true,
    type: 'demo',
    badges: ['Top Agency', 'Premium Listings'],
    metrics: {
      revenue_mtd: 175000,
      revenue_change_pct: 22.5,
      profit_margin_pct: 38,
      active_listings: 45,
      days_on_market: 28,
      avg_sale_price: 2850000,
      commission_ytd: 1850000
    }
  },

  // ============================================
  // CONSTRUCTION - BuildRight Construction
  // ============================================
  construction: {
    contact_id: 'demo-buildright',
    business_id: 'construction_demo_001',
    company: 'BuildRight Construction',
    name: 'David Williams',
    role: 'Director',
    industry: 'construction',
    sub_industry: 'commercial_builder',
    email: 'demo@buildright.com.au',
    phone: '(02) 9444 5555',
    mrr: 420000,
    arr: 5040000,
    tier: 'enterprise',
    health_score: 85,
    staff_count: 85,
    customer_count: 120,
    location: 'Parramatta, Sydney NSW 2150',
    modules: ['marketing', 'operations', 'financial', 'staff', 'compliance', 'projections'],
    tools: ['crm', 'project_management', 'safety', 'invoicing'],
    isDemo: true,
    type: 'demo',
    badges: ['Tier 1 Builder', 'Safety Certified'],
    metrics: {
      revenue_mtd: 420000,
      revenue_change_pct: 14.2,
      profit_margin_pct: 12,
      active_projects: 8,
      on_time_delivery: 94,
      safety_score: 98,
      defect_rate: 0.8
    }
  },

  // ============================================
  // AUTOMOTIVE - Premier Auto Service
  // ============================================
  automotive: {
    contact_id: 'demo-premier-auto',
    business_id: 'automotive_demo_001',
    company: 'Premier Auto Service',
    name: 'Tony Russo',
    role: 'Owner',
    industry: 'automotive',
    sub_industry: 'auto_service',
    email: 'demo@premierauto.com.au',
    phone: '(02) 9333 4444',
    mrr: 125000,
    arr: 1500000,
    tier: 'professional',
    health_score: 83,
    staff_count: 24,
    customer_count: 3200,
    location: 'Alexandria, Sydney NSW 2015',
    modules: ['marketing', 'operations', 'financial', 'customer', 'inventory'],
    tools: ['crm', 'booking', 'parts_management', 'invoicing'],
    isDemo: true,
    type: 'demo',
    badges: ['Authorized Service', 'Fleet Partner'],
    metrics: {
      revenue_mtd: 125000,
      revenue_change_pct: 7.5,
      profit_margin_pct: 32,
      jobs_completed: 480,
      avg_repair_value: 385,
      repeat_customers: 78,
      reviews_score: 4.6
    }
  },

  // ============================================
  // EDUCATION - Bright Minds Academy
  // ============================================
  education: {
    contact_id: 'demo-bright-minds',
    business_id: 'education_demo_001',
    company: 'Bright Minds Academy',
    name: 'Rebecca Lee',
    role: 'Principal',
    industry: 'education',
    sub_industry: 'tutoring',
    email: 'demo@brightminds.com.au',
    phone: '(02) 9777 8888',
    mrr: 85000,
    arr: 1020000,
    tier: 'professional',
    health_score: 90,
    staff_count: 32,
    customer_count: 650,
    location: 'North Sydney, NSW 2060',
    modules: ['marketing', 'operations', 'financial', 'staff', 'customer'],
    tools: ['email_marketing', 'crm', 'scheduling', 'student_portal'],
    isDemo: true,
    type: 'demo',
    badges: ['Education Excellence', 'Top Rated'],
    metrics: {
      revenue_mtd: 85000,
      revenue_change_pct: 12.8,
      profit_margin_pct: 35,
      active_students: 650,
      class_attendance: 94,
      parent_satisfaction: 4.9,
      retention_rate: 92
    }
  },

  // ============================================
  // BEAUTY - Glow Beauty Studio
  // ============================================
  beauty: {
    contact_id: 'demo-glow-beauty',
    business_id: 'beauty_demo_001',
    company: 'Glow Beauty Studio',
    name: 'Olivia James',
    role: 'Creative Director',
    industry: 'beauty',
    sub_industry: 'beauty_salon',
    email: 'demo@glowbeauty.com.au',
    phone: '(02) 9666 7777',
    mrr: 72000,
    arr: 864000,
    tier: 'essentials',
    health_score: 88,
    staff_count: 12,
    customer_count: 1850,
    location: 'Paddington, Sydney NSW 2021',
    modules: ['marketing', 'operations', 'financial', 'customer'],
    tools: ['email_marketing', 'crm', 'appointments', 'loyalty'],
    isDemo: true,
    type: 'demo',
    badges: ['Beauty Excellence', 'Top Salon'],
    metrics: {
      revenue_mtd: 72000,
      revenue_change_pct: 16.5,
      profit_margin_pct: 42,
      appointments_weekly: 280,
      avg_service_value: 125,
      rebooking_rate: 72,
      retail_attach: 35
    }
  },

  // ============================================
  // MANUFACTURING - Precision Parts Co
  // ============================================
  manufacturing: {
    contact_id: 'demo-precision-parts',
    business_id: 'manufacturing_demo_001',
    company: 'Precision Parts Co',
    name: 'Robert Chen',
    role: 'Managing Director',
    industry: 'manufacturing',
    sub_industry: 'precision_manufacturing',
    email: 'demo@precisionparts.com.au',
    phone: '(02) 9222 3333',
    mrr: 380000,
    arr: 4560000,
    tier: 'enterprise',
    health_score: 82,
    staff_count: 120,
    customer_count: 85,
    location: 'Wetherill Park, Sydney NSW 2164',
    modules: ['operations', 'financial', 'staff', 'compliance', 'inventory'],
    tools: ['erp', 'quality_management', 'supply_chain', 'invoicing'],
    isDemo: true,
    type: 'demo',
    badges: ['ISO Certified', 'Industry Leader'],
    metrics: {
      revenue_mtd: 380000,
      revenue_change_pct: 6.2,
      profit_margin_pct: 18,
      production_efficiency: 94.5,
      quality_score: 99.2,
      on_time_delivery: 97,
      inventory_turns: 8.5
    }
  },

  // ============================================
  // TECHNOLOGY - Digital Wave Agency
  // ============================================
  technology: {
    contact_id: 'demo-digital-wave',
    business_id: 'technology_demo_001',
    company: 'Digital Wave Agency',
    name: 'Alex Turner',
    role: 'CEO',
    industry: 'technology',
    sub_industry: 'digital_agency',
    email: 'demo@digitalwave.com.au',
    phone: '(02) 9111 2222',
    mrr: 165000,
    arr: 1980000,
    tier: 'professional',
    health_score: 91,
    staff_count: 42,
    customer_count: 65,
    location: 'Richmond, Melbourne VIC 3121',
    modules: ['marketing', 'operations', 'financial', 'staff', 'customer', 'projections'],
    tools: ['crm', 'project_management', 'time_tracking', 'invoicing'],
    isDemo: true,
    type: 'demo',
    badges: ['Tech Excellence', 'Innovation Award'],
    metrics: {
      revenue_mtd: 165000,
      revenue_change_pct: 24.5,
      profit_margin_pct: 38,
      active_projects: 28,
      utilization_rate: 82,
      client_retention: 94,
      nps_score: 78
    }
  },

  // ============================================
  // QSR - The Daily Grill (Multi-Location)
  // ============================================
  qsr: {
    contact_id: 'demo-daily-grill',
    business_id: 'qsr_demo_001',
    company: 'The Daily Grill',
    name: 'Karen Whitfield',
    role: 'Director',
    industry: 'qsr',
    sub_industry: 'quick_service_restaurant',
    email: 'demo@thedailygrill.com.au',
    phone: '1300 GRILL',
    mrr: 265000,
    arr: 3180000,
    tier: 'enterprise',
    health_score: 88,
    staff_count: 72,
    customer_count: 18500,
    location: 'Penrith, NSW 2750',
    isMultiLocation: true,
    locationCount: 5,
    modules: ['marketing', 'operations', 'financial', 'staff', 'compliance', 'customer', 'inventory'],
    tools: ['crm', 'pos', 'drive_through', 'roster_management', 'delivery'],
    isDemo: true,
    type: 'demo',
    badges: ['Multi-Location', 'QSR Excellence', 'Drive-Through'],
    metrics: {
      revenue_mtd: 265000,
      revenue_change_pct: 9.8,
      profit_margin_pct: 18,
      locations: 5,
      avg_splh: 61.26,
      drive_through_locations: 2,
      avg_transaction: 20.20,
      total_daily_transactions: 2660
    }
  },

  // ============================================
  // TECHNOLOGY (Corvana) - Corvana Platform
  // ============================================
  corvana_tech: {
    contact_id: 'demo-corvana-tech',
    business_id: 'corvana_tech_demo_001',
    company: 'Corvana',
    name: 'Christian Vasquez',
    role: 'Founder & CEO',
    industry: 'technology',
    sub_industry: 'saas_platform',
    email: 'demo@corvana.com.au',
    phone: '(02) 8006 1200',
    mrr: 142000,
    arr: 1704000,
    tier: 'professional',
    health_score: 94,
    staff_count: 28,
    customer_count: 540,
    location: 'Surry Hills, Sydney NSW 2010',
    modules: ['marketing', 'operations', 'financial', 'staff', 'customer', 'projections', 'market_intel'],
    tools: ['crm', 'product_analytics', 'billing', 'support_desk'],
    isDemo: true,
    type: 'demo',
    badges: ['SaaS Platform', 'AI-Native', 'Innovation Award'],
    metrics: {
      revenue_mtd: 142000,
      revenue_change_pct: 32.5,
      profit_margin_pct: 45,
      active_customers: 540,
      churn_rate: 2.1,
      nps_score: 82,
      mrr_growth: 8.5
    }
  },

  // ============================================
  // FINANCIAL - Wealth Partners Advisory
  // ============================================
  finance: {
    contact_id: 'demo-wealth-partners',
    business_id: 'financial_demo_001',
    company: 'Wealth Partners Advisory',
    name: 'Jennifer Wu',
    role: 'Senior Partner',
    industry: 'finance',
    sub_industry: 'financial_advisory',
    email: 'demo@wealthpartners.com.au',
    phone: '(02) 9888 9999',
    mrr: 285000,
    arr: 3420000,
    tier: 'enterprise',
    health_score: 93,
    staff_count: 18,
    customer_count: 320,
    location: 'Collins St, Melbourne VIC 3000',
    modules: ['marketing', 'operations', 'financial', 'customer', 'compliance', 'projections'],
    tools: ['crm', 'portfolio_management', 'compliance', 'reporting'],
    isDemo: true,
    type: 'demo',
    badges: ['AFSL Licensed', 'Top Advisor'],
    metrics: {
      revenue_mtd: 285000,
      revenue_change_pct: 11.8,
      profit_margin_pct: 52,
      aum: 485000000,
      client_retention: 98,
      compliance_score: 100,
      nps_score: 82
    }
  },

  // ============================================
  // TOURISM/TRAVEL - Wanderlust Travel
  // ============================================
  tourism: {
    contact_id: 'demo-wanderlust',
    business_id: 'travel_demo_001',
    company: 'Wanderlust Travel',
    name: 'Sophie Anderson',
    role: 'Director',
    industry: 'tourism',
    sub_industry: 'travel_agency',
    email: 'demo@wanderlusttravel.com.au',
    phone: '1300 TRAVEL',
    mrr: 92000,
    arr: 1104000,
    tier: 'professional',
    health_score: 85,
    staff_count: 15,
    customer_count: 2800,
    location: 'Cairns, QLD 4870',
    modules: ['marketing', 'operations', 'financial', 'customer'],
    tools: ['crm', 'booking_system', 'itinerary', 'payments'],
    isDemo: true,
    type: 'demo',
    badges: ['ATAS Accredited', 'Luxury Specialist'],
    metrics: {
      revenue_mtd: 92000,
      revenue_change_pct: 32.5,
      profit_margin_pct: 15,
      bookings_monthly: 185,
      avg_booking_value: 4850,
      repeat_customers: 65,
      reviews_score: 4.9
    }
  },
  mining: {
    name: 'Ben Crawford',
    email: 'ben@ironoreresources.com.au',
    company: 'IronOre Resources',
    business_id: 'mining_demo_001',
    industry: 'mining',
    sub_industry: 'metal_ore',
    anzsic_code: 'B',
    subscription_tier: 'enterprise',
    monthly_revenue: 425000,
    staff_count: 120,
    health_score: 82,
    integrations: ['fleet_management', 'safety_compliance', 'erp'],
    kpis: {
      production_volume: '2.4M tonnes/year',
      safety_incidents: 2,
      equipment_uptime: '94%',
      cost_per_tonne: '$48',
      eofy_forecast: '$5.1M'
    }
  },
  utilities: {
    name: 'Sarah Mitchell',
    email: 'sarah@southernenergyco.com.au',
    company: 'Southern Energy Co',
    business_id: 'utilities_demo_001',
    industry: 'utilities',
    sub_industry: 'electricity_supply',
    anzsic_code: 'D',
    subscription_tier: 'professional',
    monthly_revenue: 280000,
    staff_count: 85,
    health_score: 88,
    integrations: ['smart_metering', 'field_service', 'billing'],
    kpis: {
      service_uptime: '99.7%',
      customer_satisfaction: '4.3/5',
      energy_output: '180MW',
      outage_response: '12min avg',
      eofy_forecast: '$3.4M'
    }
  },
  admin_services: {
    name: 'Mark Thompson',
    email: 'mark@apexstaffing.com.au',
    company: 'Apex Staffing Solutions',
    business_id: 'admin_services_demo_001',
    industry: 'admin_services',
    sub_industry: 'recruitment',
    anzsic_code: 'N',
    subscription_tier: 'professional',
    monthly_revenue: 95000,
    staff_count: 32,
    health_score: 75,
    integrations: ['job_board', 'background_check', 'payroll'],
    kpis: {
      client_retention: '87%',
      staff_utilization: '78%',
      placements_monthly: 45,
      response_time: '2.1hrs',
      eofy_forecast: '$1.1M'
    }
  },
  public_admin: {
    name: 'Dr. Rachel Nguyen',
    email: 'rachel@nsw.gov.au',
    company: 'NSW Department of Communities',
    business_id: 'public_admin_demo_001',
    industry: 'public_admin',
    sub_industry: 'state_government',
    anzsic_code: 'O',
    subscription_tier: 'enterprise',
    monthly_revenue: 0,
    staff_count: 250,
    health_score: 90,
    integrations: ['case_management', 'compliance', 'reporting'],
    kpis: {
      service_delivery: '96%',
      compliance_rate: '99.2%',
      public_satisfaction: '4.1/5',
      response_time: '1.8 days',
      budget_adherence: '98%'
    }
  }
};

/**
 * Get all demo customers as an array
 */
export const getAllDemoCustomers = () => Object.values(DEMO_CUSTOMERS);

/**
 * Get demo customer by industry
 */
export const getDemoCustomerByIndustry = (industry) => DEMO_CUSTOMERS[industry] || null;

/**
 * Get demo customer by business ID
 */
export const getDemoCustomerByBusinessId = (businessId) => {
  return getAllDemoCustomers().find(c => c.business_id === businessId) || null;
};

/**
 * Get demo customer login credentials
 */
export const getDemoCredentials = (industry) => {
  const customer = DEMO_CUSTOMERS[industry];
  if (!customer) return null;
  return {
    email: customer.email,
    password: 'DemoPassword123!',
    business_id: customer.business_id,
    name: customer.name,
    company: customer.company
  };
};

/**
 * Get industry config for a demo customer
 */
export const getDemoIndustryConfig = (industry) => {
  return INDUSTRY_UI_CONFIG[industry] || INDUSTRY_UI_CONFIG.hospitality;
};

/**
 * List of all available industries with demo customers
 */
export const DEMO_INDUSTRIES = Object.keys(DEMO_CUSTOMERS);

export default DEMO_CUSTOMERS;
